import React, {useState} from "react";
import styled from "styled-components";
import {Link} from "react-router-dom";
import {BoardContainer, TitleContainer} from "../../assets/GlobalStyles";

const NOTION_GUIDE_URL = "https://www.notion.so/API-2afbe20d845e8002a124c20d307295da?source=copy_link";

const API_DOMAIN = "https://dsp.oaple.co.kr";

const VAST_EXAMPLE = `<VAST xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="vast.xsd" version="3.0">
  <Ad id="b8fdd5a0-9ea5-4f4e-85de-51d4c143827a">
    <InLine>
      <AdSystem version="1.0">oaple</AdSystem>
      <AdTitle><![CDATA[오디오 캠페인]]></AdTitle>
      <Impression><![CDATA[https://dsp.oaple.co.kr/audio/imp?mediaScriptNo=49003821-554a-437a-b1eb-1512a40570d1&campaignId=b8fdd5a0-9ea5-4f4e-85de-51d4c143827a&creativeSeq=1]]></Impression>
      <Creatives>
        <Creative id="1" adID="b8fdd5a0-9ea5-4f4e-85de-51d4c143827a_1">
          <Linear>
            <Duration>00:00:07</Duration>
            <MediaFiles>
              <MediaFile id="mediaFileId" delivery="progressive" type="audio/mpeg">
                <![CDATA[https://cdn.example.com/audio/ad.mp3]]>
              </MediaFile>
            </MediaFiles>
          </Linear>
        </Creative>
        <Creative id="1-companion" sequence="1">
          <CompanionAds required="none">
            <Companion id="img1" width="640" height="640">
              <StaticResource creativeType="image/jpeg"><![CDATA[https://cdn.example.com/audio/banner.jpg]]></StaticResource>
              <TrackingEvents>
                <Tracking event="creativeView"><![CDATA[https://dsp.oaple.co.kr/audio/companion/view?...]]></Tracking>
              </TrackingEvents>
              <CompanionClickThrough><![CDATA[https://advertiser.example.com/landing]]></CompanionClickThrough>
              <CompanionClickTracking><![CDATA[https://dsp.oaple.co.kr/audio/companion/click?...]]></CompanionClickTracking>
            </Companion>
          </CompanionAds>
        </Creative>
      </Creatives>
    </InLine>
  </Ad>
</VAST>`;

const VAST_NOAD_EXAMPLE = `<VAST xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="vast.xsd" version="3.0">
  <!-- 광고 없음: <Ad> 노드가 포함되지 않습니다 -->
</VAST>`;

const JSON_EXAMPLE = `{
  "response": "audio",
  "ads": [
    {
      "duration": 7,
      "impression": "https://dsp.oaple.co.kr/audio/imp?mediaScriptNo=49003821-554a-437a-b1eb-1512a40570d1&campaignId=b8fdd5a0-9ea5-4f4e-85de-51d4c143827a&creativeSeq=1",
      "mediaFile": "https://cdn.example.com/audio/ad.mp3",
      "companionImage": "https://cdn.example.com/audio/banner.jpg",
      "companionImageWidth": 640,
      "companionImageHeight": 640,
      "companionImageMimeType": "image/jpeg",
      "companionLandingUrl": "https://advertiser.example.com/landing"
    }
  ]
}`;

const JSON_NOAD_EXAMPLE = `{
  "response": "noAd",
  "ads": null
}`;

const REQUEST_PARAMS = [
  {name: "mediaScriptNo", desc: "광고를 요청할 매체 지면 UUID", required: true, example: "49003821-554a-437a-b1eb-1512a40570d1"},
  {name: "output", desc: "응답 형식(대소문자 무관)", required: true, example: "xml / json"},
  {name: "gender", desc: "사용자 성별. M은 남성, F는 여성", required: false, example: "M / F"},
  {name: "age", desc: "사용자 만 나이. 10세 단위 타기팅에 사용", required: false, example: "30"},
  {name: "country", desc: "시·도 지역코드 또는 등록된 명칭", required: false, example: "11 또는 서울특별시"},
  {name: "city", desc: "시·군·구 지역코드 또는 등록된 명칭", required: false, example: "11680 또는 강남구"},
  {name: "region", desc: "행정동 지역코드 또는 등록된 명칭", required: false, example: "11680640 또는 역삼1동"},
  {name: "zone", desc: "매체가 자체적으로 정의한 지역 식별값", required: false, example: "3003 또는 신현점"},
];

const VAST_FIELDS = [
  {group: "루트 노드", tag: "<VAST version>", desc: "현재 API가 반환하는 VAST 버전", required: true, example: "3.0"},
  {group: "광고 단위", tag: "<Ad id>", desc: "캠페인 UUID", required: true, example: "b8fdd5a0-9ea5-4f4e-85de-51d4c143827a"},
  {group: "", tag: "<InLine>", desc: "광고 메타데이터와 소재 정보", required: true, example: "—"},
  {group: "광고 시스템 정보", tag: "<AdSystem>", desc: "광고 송출 시스템 이름 및 버전", required: true, example: "Oaple / 1.0"},
  {group: "", tag: "<AdTitle>", desc: "캠페인명", required: true, example: "오디오 캠페인"},
  {group: "노출 트래킹", tag: "<Impression>", desc: "재생 시 호출할 /audio/imp URL", required: true, example: `${API_DOMAIN}/audio/imp?...`},
  {group: "크리에이티브 묶음", tag: "<Creatives>", desc: "광고 소재 묶음 노드", required: true, example: "—"},
  {group: "", tag: "<Creative>", desc: "개별 광고 크리에이티브 단위", required: true, example: "—"},
  {group: "선형 광고", tag: "<Linear>", desc: "비디오/오디오 스트림 형태 광고 정의", required: true, example: "—"},
  {group: "", tag: "<Duration>", desc: "광고 길이(HH:mm:ss)", required: true, example: "00:00:07"},
  {group: "", tag: "<MediaFiles>", desc: "실제 광고 미디어 파일 리스트", required: true, example: "—"},
  {group: "", tag: "<MediaFile>", desc: "광고 오디오 파일 URL", required: true, example: "<MediaFile type=\"audio/mpeg\">https://cdn.example.com/audio/ad.mp3</MediaFile>"},
  {group: "", tag: "속성", desc: "고정 미디어 속성", required: true, example: "id=\"mediaFileId\" type=\"audio/mpeg\" delivery=\"progressive\""},
  {group: "보조 이미지", tag: "<CompanionAds>", desc: "오디오 소재에 보조 이미지가 등록된 경우에만 포함", required: false, example: "required=\"none\""},
  {group: "", tag: "<Companion>", desc: "보조 이미지 크기와 리소스·트래킹 정보", required: false, example: "width=\"640\" height=\"640\""},
  {group: "", tag: "<StaticResource>", desc: "보조 이미지 URL과 MIME 타입", required: false, example: "creativeType=\"image/jpeg\""},
  {group: "", tag: "<CompanionClickThrough>", desc: "보조 이미지 전용 랜딩 URL. 등록된 경우에만 포함", required: false, example: "https://advertiser.example.com/landing"},
  {group: "종료 태그", tag: "</InLine>, </Ad>, </VAST>", desc: "각 노드 종료 태그", required: true, example: "—"},
];

const JSON_FIELDS = [
  {tag: "response", desc: "광고가 있으면 audio, 없으면 noAd", required: true},
  {tag: "ads", desc: "광고 객체 배열. 광고가 없으면 null", required: false},
  {tag: "duration", desc: "오디오 재생 시간(초, 정수)", required: true},
  {tag: "impression", desc: "재생 시 호출할 노출 추적 URL", required: true},
  {tag: "mediaFile", desc: "오디오 파일 경로", required: true},
  {tag: "companionImage", desc: "선택형 보조 이미지 경로", required: false},
  {tag: "companionImageWidth / companionImageHeight", desc: "보조 이미지의 실제 픽셀 크기", required: false},
  {tag: "companionImageMimeType", desc: "보조 이미지 MIME 타입", required: false},
  {tag: "companionLandingUrl", desc: "보조 이미지 클릭 랜딩 URL", required: false},
];

const VAST_SUMMARY = [
  {tag: "<VAST version=\"3.0\">", desc: "현재 광고 API의 VAST 버전"},
  {tag: "<Ad id=\"...\">", desc: "광고 식별자"},
  {tag: "<InLine>", desc: "광고 메타데이터와 소재를 직접 포함"},
  {tag: "<Creatives> → <Creative> → <Linear>", desc: "선형 광고 (오디오도 선형 광고 형태)"},
  {tag: "<Duration>", desc: "광고 지속 시간 (예: 00:00:07)"},
  {tag: "<MediaFiles> → <MediaFile>", desc: "미디어 파일 URL, MIME 타입 (예: audio/mpeg)"},
  {tag: "<Impression>", desc: "노출 추적 URL"},
  {tag: "<CompanionAds> → <Companion>", desc: "등록된 경우에만 제공되는 보조 이미지와 랜딩 URL"},
];

function CollapsibleSection({title, desc, defaultOpen = true, children}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Section>
      <SectionHeader onClick={() => setOpen(prev => !prev)} $open={open}>
        <SectionHeaderText>
          <SectionTitle>{title}</SectionTitle>
          {desc && <SectionDesc>{desc}</SectionDesc>}
        </SectionHeaderText>
        <SectionChevron $open={open}>▾</SectionChevron>
      </SectionHeader>
      {open && <SectionBody>{children}</SectionBody>}
    </Section>
  );
}

export default function ApiGuide() {
  return (
    <main>
      <BoardContainer>
        <TitleContainer>
          <div>
            <h1>API 연동 가이드</h1>
          </div>
          <TitleActions>
            <TestPageLink to="/board/apiTest">API 테스트해보기</TestPageLink>
            <SourceLink href={NOTION_GUIDE_URL} target="_blank" rel="noopener noreferrer">
              원본 문서 보기 ↗
            </SourceLink>
          </TitleActions>
        </TitleContainer>

        <Lead>매체 지면에서 오디오 광고를 수신하기 위한 광고 송출 API 요청/응답 규격을 안내합니다.</Lead>

        <CollapsibleSection title="1. Request" desc="광고 요청 시 사용하는 도메인과 파라미터입니다.">
            <MetaRow>
              <MetaLabel>Domain</MetaLabel>
              <code>{API_DOMAIN}</code>
            </MetaRow>
            <MetaRow>
              <MetaLabel>API URI</MetaLabel>
              <code>/audio/ad</code>
            </MetaRow>
            <MetaRow>
              <MetaLabel>Method</MetaLabel>
              <code>GET</code>
            </MetaRow>

            <FieldTable>
              <thead>
                <tr>
                  <th>태그명 / 필드</th>
                  <th>설명</th>
                  <th>필수 여부</th>
                  <th>예시 값</th>
                </tr>
              </thead>
              <tbody>
                {REQUEST_PARAMS.map(param => (
                  <tr key={param.name}>
                    <td><code>{param.name}</code></td>
                    <td>{param.desc}</td>
                    <td>{param.required ? <RequiredBadge>필수</RequiredBadge> : <OptionalBadge>선택</OptionalBadge>}</td>
                    <td>{param.example}</td>
                  </tr>
                ))}
              </tbody>
            </FieldTable>

            <ExampleBlock>
              <ExampleLabel>예시</ExampleLabel>
              <pre>{`xml  : ${API_DOMAIN}/audio/ad?mediaScriptNo=49003821-554a-437a-b1eb-1512a40570d1&output=xml\njson : ${API_DOMAIN}/audio/ad?mediaScriptNo=49003821-554a-437a-b1eb-1512a40570d1&output=json\n지역명 사용 : ${API_DOMAIN}/audio/ad?mediaScriptNo=49003821-554a-437a-b1eb-1512a40570d1&output=json&gender=M&age=30&country=서울특별시&city=강남구&region=역삼1동\n지역코드 사용 : ${API_DOMAIN}/audio/ad?mediaScriptNo=49003821-554a-437a-b1eb-1512a40570d1&output=json&gender=M&age=30&country=11&city=11680&region=11680640\n커스텀 지역 사용 : ${API_DOMAIN}/audio/ad?mediaScriptNo=49003821-554a-437a-b1eb-1512a40570d1&output=json&zone=3003`}</pre>
            </ExampleBlock>
            <NoteBox>
              <code>gender</code>, <code>age</code>, <code>country</code>, <code>city</code>, <code>region</code>, <code>zone</code>은 선택 파라미터입니다.
              성별은 <code>M</code>/<code>F</code>, 나이는 정수로 전달합니다. 지역은 <code>country=시·도</code>, <code>city=시·군·구</code>, <code>region=행정동</code> 순서이며 각 파라미터에 등록된 지역코드 또는 지역명 중 하나를 사용할 수 있습니다. 지역코드와 지역명은 혼합해서 전달할 수도 있습니다.
              <code>zone</code>은 표준 행정구역으로 표현하기 어려운 역, 노선, 매장, 시설 등 매체별 커스텀 지역 식별값이며, 값의 형식과 의미는 매체별 연동 규격을 따릅니다. 표준 지역 파라미터와 함께 전달할 수도 있습니다.
            </NoteBox>
        </CollapsibleSection>

        <CollapsibleSection title="2. Response — XML (VAST 규격)" desc="output=xml 요청 시 현재 DSP 구현 기준 VAST 3.0 XML을 반환합니다.">
            <FieldTable>
              <thead>
                <tr>
                  <th>구분</th>
                  <th>태그명 / 필드</th>
                  <th>설명</th>
                  <th>필수 여부</th>
                  <th>예시 값</th>
                </tr>
              </thead>
              <tbody>
                {VAST_FIELDS.map((field, index) => (
                  <tr key={index}>
                    <td>{field.group}</td>
                    <td><code>{field.tag}</code></td>
                    <td>{field.desc}</td>
                    <td>{field.required
                        ? <RequiredBadge>{field.requiredLabel || "필수"}</RequiredBadge>
                        : <OptionalBadge>선택</OptionalBadge>}</td>
                    <td>{field.example}</td>
                  </tr>
                ))}
              </tbody>
            </FieldTable>

            <ExampleBlock>
              <ExampleLabel>예시 · 광고 있을 시</ExampleLabel>
              <pre>{VAST_EXAMPLE}</pre>
            </ExampleBlock>
            <ExampleBlock>
              <ExampleLabel>예시 · 광고 없을 시 (noAd)</ExampleLabel>
              <pre>{VAST_NOAD_EXAMPLE}</pre>
            </ExampleBlock>
            <NoteBox><code>Impression</code> URL은 플레이어가 실제 광고 재생을 시작할 때 한 번 호출해야 합니다. 광고가 없으면 <code>&lt;Ad&gt;</code> 노드가 포함되지 않습니다.</NoteBox>
        </CollapsibleSection>

        <CollapsibleSection title="3. Response — JSON" desc="output=json 요청 시 간략화된 JSON 응답을 반환합니다.">
            <FieldTable>
              <thead>
                <tr>
                  <th>태그명 / 필드</th>
                  <th>설명</th>
                  <th>필수 여부</th>
                </tr>
              </thead>
              <tbody>
                {JSON_FIELDS.map(field => (
                  <tr key={field.tag}>
                    <td><code>{field.tag}</code></td>
                    <td>{field.desc}</td>
                    <td>{field.required ? <RequiredBadge>필수</RequiredBadge> : <OptionalBadge>선택</OptionalBadge>}</td>
                  </tr>
                ))}
              </tbody>
            </FieldTable>

            <ExampleBlock>
              <ExampleLabel>예시 · 광고 있을 시</ExampleLabel>
              <pre>{JSON_EXAMPLE}</pre>
            </ExampleBlock>
            <ExampleBlock>
              <ExampleLabel>예시 · 광고 없을 시 (noAd)</ExampleLabel>
              <pre>{JSON_NOAD_EXAMPLE}</pre>
            </ExampleBlock>
            <NoteBox>광고가 없을 경우 <code>response</code>는 <code>noAd</code>, <code>ads</code>는 <code>null</code>입니다. 광고가 있으면 <code>response</code>는 <code>audio</code>입니다.</NoteBox>
        </CollapsibleSection>

        <CollapsibleSection title="참고 · 현재 DSP VAST 응답 구조">
            <RefList>
              <li>현재 DSP는 <code>VAST 3.0</code>과 <code>InLine</code> 형식으로 응답합니다.</li>
              <li>오디오 파일은 <code>audio/mpeg</code>, <code>progressive</code> 형식으로 전달됩니다.</li>
              <li><code>Impression</code>에는 DSP 노출 집계 API 주소가 포함됩니다.</li>
              <li>보조 이미지가 등록된 오디오 소재는 별도 <code>Creative/CompanionAds</code>를 함께 반환합니다.</li>
              <li>보조 이미지가 없는 기존 오디오 소재의 VAST 구조는 변경되지 않습니다.</li>
            </RefList>

            <SubTitle>VAST XML 구조 내 주요 요소</SubTitle>
            <FieldTable>
              <thead>
                <tr>
                  <th>태그</th>
                  <th>설명</th>
                </tr>
              </thead>
              <tbody>
                {VAST_SUMMARY.map(item => (
                  <tr key={item.tag}>
                    <td><code>{item.tag}</code></td>
                    <td>{item.desc}</td>
                  </tr>
                ))}
              </tbody>
            </FieldTable>
        </CollapsibleSection>
      </BoardContainer>
    </main>
  );
}

const TitleActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--color-primary-border);
  border-radius: 8px;
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
  font-size: var(--font-size-13);
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: var(--color-primary-muted);
    border-color: var(--color-primary-focus);
  }
`;

const TestPageLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #bbf0d1;
  border-radius: 8px;
  background: #ecfdf5;
  color: var(--color-success);
  font-size: var(--font-size-13);
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: #d1fae5;
    border-color: #86efac;
  }
`;

const Lead = styled.p`
  margin: 4px 0 20px;
  color: #666e76;
  font-size: var(--font-size-14);
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-surface);
  box-shadow: 0 5px 18px rgba(31, 41, 55, .045);

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: ${p => (p.$open ? "1px solid #e9edf1" : "none")};
  background: #fafbfc;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #f3f5f7;
  }
`;

const SectionHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const SectionChevron = styled.span`
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-15);
  transform: rotate(${p => (p.$open ? "0deg" : "-90deg")});
  transition: transform .15s ease;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #202a35;
  font-size: var(--font-size-16);
  font-weight: 750;
`;

const SectionDesc = styled.p`
  margin: 5px 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-13);
  line-height: 1.5;
`;

const SectionBody = styled.div`
  padding: 18px 20px 20px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: var(--font-size-13);

  code {
    padding: 3px 8px;
    border-radius: 6px;
    background: #f2f7fd;
    color: var(--color-primary-strong);
    font-weight: 700;
  }
`;

const MetaLabel = styled.span`
  min-width: 70px;
  color: #67707a;
  font-weight: 700;
`;

const FieldTable = styled.table`
  width: 100%;
  margin: 14px 0;
  border: 1px solid #e4e9ee;
  border-collapse: collapse;
  border-radius: 8px;
  font-size: var(--font-size-13);
  overflow: hidden;

  th {
    padding: 10px 12px;
    border-bottom: 2px solid #8bb8ea;
    background: #f2f7fd;
    color: #34475a;
    font-weight: 750;
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: 9px 12px;
    border-bottom: 1px solid #edf1f4;
    color: var(--color-text);
    vertical-align: top;
    word-break: break-all;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  code {
    color: var(--color-text-strong);
    font-weight: 700;
  }
`;

const RequiredBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
  font-size: var(--font-size-11);
  font-weight: 750;
  white-space: nowrap;
`;

const OptionalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f4f7;
  color: #6b7684;
  font-size: var(--font-size-11);
  font-weight: 750;
`;

const ExampleBlock = styled.div`
  margin-top: 12px;
  overflow: hidden;
  border: 1px solid #e3e8ee;
  border-radius: 8px;

  pre {
    margin: 0;
    padding: 14px 16px;
    overflow-x: auto;
    background: #1e2530;
    color: #d7e0ea;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    font-size: var(--font-size-12-5);
    line-height: 1.6;
    white-space: pre;
  }
`;

const ExampleLabel = styled.div`
  padding: 7px 14px;
  background: #eef2f6;
  color: #4b5563;
  font-size: var(--font-size-12);
  font-weight: 750;
`;

const NoteBox = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  border: 1px solid #fde68a;
  border-radius: 7px;
  background: #fffbeb;
  color: #92400e;
  font-size: var(--font-size-13);
  line-height: 1.6;

  code {
    padding: 1px 5px;
    border-radius: 4px;
    background: rgba(146, 64, 14, .1);
    font-weight: 700;
  }
`;

const RefList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #4a5561;
  font-size: var(--font-size-13);
  line-height: 1.9;

  code {
    color: var(--color-text-strong);
    font-weight: 700;
  }
`;

const SubTitle = styled.h3`
  margin: 18px 0 0;
  color: #2d343b;
  font-size: var(--font-size-14);
  font-weight: 750;
`;
